import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useImages, useSectionMeta } from "@/context/SiteConfigContext";
import { MaskLine } from "@/components/site/Reveal";
import { SectionButtons } from "@/components/site/primitives";

export default function Hero() {
  const { t } = useLang();
  const h = t.hero;
  const images = useImages();
  const heroSrc = images.hero_portrait || null;
  const [imgLoaded, setImgLoaded] = useState(false);
  const meta = useSectionMeta("hero");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.04, 1.16]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "7%"]);
  const imgOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.4]);

  return (
    <section
      id="top"
      data-full
      ref={ref}
      className="relative isolate min-h-[100svh] bg-char text-paper overflow-hidden flex flex-col"
    >
      {/* Unified full-bleed portrait — loaded ONLY from the DB image, faded in over
          the theme background (skeleton) so the visitor never sees an old placeholder */}
      <motion.div style={{ scale, y, opacity: imgOpacity }} className="absolute inset-[-7%] z-0 will-change-transform bg-char">
        {heroSrc && (
          <img
            key={heroSrc}
            src={heroSrc}
            alt="Oana Mudura"
            fetchPriority="high"
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            className={`w-full h-full object-cover transition-opacity duration-700 ease-out ${imgLoaded ? "opacity-100" : "opacity-0"}`}
            style={{ objectPosition: "74% 18%" }}
          />
        )}
      </motion.div>

      {/* Banded scrims — protect only the top (nav/roles) and bottom (statement/CTA)
          bands and leave the centre clear so the blended name reacts to the photo */}
      <div className="absolute inset-x-0 top-0 h-[32%] z-[1] bg-gradient-to-b from-char/85 via-char/25 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-[42%] z-[1] bg-gradient-to-t from-char via-char/60 to-transparent" />

      {/* Name — massive, overlapping the portrait, blended into the image via mix-blend-difference */}
      <div
        className="relative z-[5] px-6 md:px-12 lg:px-20 pt-32 md:pt-44 pointer-events-none select-none"
        style={{ mixBlendMode: "difference" }}
      >
        <h1 className="display leading-[0.78] text-white">
          <span className="block text-[19vw] md:text-[16vw] lg:text-[14vw]">
            <MaskLine delay={0.05}>{h.name[0]}</MaskLine>
          </span>
          <span className="block text-[19vw] md:text-[16vw] lg:text-[14vw] pl-[8vw] md:pl-[18vw]">
            <MaskLine delay={0.18}>{h.name[1]}</MaskLine>
          </span>
        </h1>
      </div>

      {/* Bottom: statement + intro + CTAs */}
      <div className="relative z-10 px-6 md:px-12 lg:px-20 mt-auto pt-[6vh] pb-16 md:pb-20 max-w-2xl">
        <p className="serif italic text-2xl md:text-4xl leading-[1.12] text-paper mb-8">{h.statement}</p>
        <p className="text-base md:text-lg leading-[1.6] text-paper/75 mb-9 max-w-md">{h.intro}</p>
        <SectionButtons buttons={meta.buttons} dark />
      </div>
    </section>
  );
}
